
import React, { useState, useEffect, useRef } from 'react';
import { ProjectState, Snapshot, AIIntent, AgentRole, AppSettings, TaskType, Session, Task, AIResponse } from './types';
import { calculateCPM } from './services/cpmEngine';
import { parseImportedJSON, generateTextSummary } from './services/ioService';
import GanttChart from './components/GanttChart';
import RadarView from './components/RadarView';
import AIAssistant from './components/AIAssistant';
import SettingsDialog from './components/SettingsDialog';
import ShareDialog from './components/ShareDialog';
import NotesImporter from './components/NotesImporter';
import ProjectSidebar from './components/ProjectSidebar';
import OpsCockpit from './components/OpsCockpit';
import { Layout, Clock, Database, Settings, BoxSelect, Upload, Share2, StickyNote, Target, BarChartHorizontal, Menu, PanelLeftClose, PanelLeftOpen, Activity } from 'lucide-react';

// Initial Empty State
const INITIAL_PROJECT: ProjectState = {
  id: 'proj-001',
  name: 'New DevPath Project',
  description: 'A software development project.',
  sessions: [],
  tasks: [],
  startDate: new Date().toISOString()
};

const App: React.FC = () => {
  console.log('DevPath Pro Loaded. Build Time:', (process.env as any).BUILD_TIME);
  const [projectState, setProjectState] = useState<ProjectState>(INITIAL_PROJECT);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [viewMode, setViewMode] = useState<'gantt' | 'radar' | 'ops'>('gantt');
  
  // Layout State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // App Settings
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<AppSettings>({
    useBackend: true,
    backendUrl: "", // Empty string uses local proxy to avoid CORS
    dbConfig: {
      host: "10.10.14.2",
      port: "5432",
      database: "cns_primary",
      schema: "devpath_cpm",
      user: "devpath_user",
      password: ""
    },
    notesDbConfig: {
      host: "10.10.14.2",
      port: "5432",
      database: "claude_conversations",
      user: "claude_user",
      password: ""
    }
  });

  // Dialog States
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareText, setShareText] = useState('');
  const [isNotesImportOpen, setIsNotesImportOpen] = useState(false);

  // Hidden File Input Ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hydrate from LocalStorage
  useEffect(() => {
    // Project Data
    const savedSnapshots = localStorage.getItem('devpath_snapshots');
    if (savedSnapshots) {
      const parsed = JSON.parse(savedSnapshots);
      setSnapshots(parsed);
      if (parsed.length > 0) {
        const latest = parsed[parsed.length - 1];
        const cpmTasks = calculateCPM(latest.projectState.tasks);
        setProjectState({ ...latest.projectState, tasks: cpmTasks });
      }
    }

    // Config Data
    const savedSettings = localStorage.getItem('devpath_settings');
    if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Persistence
  useEffect(() => {
    localStorage.setItem('devpath_snapshots', JSON.stringify(snapshots));
  }, [snapshots]);

  useEffect(() => {
    localStorage.setItem('devpath_settings', JSON.stringify(settings));
  }, [settings]);

  // Handler for AI Updates
  const handleAIUpdate = (newState: ProjectState, reasoning: string, intent: AIIntent, tagName: string, agent: AgentRole, migrationSQL?: string) => {
    const calculatedTasks = calculateCPM(newState.tasks);
    
    // Merge new migrationSQL or keep existing if not handled yet
    const finalMigration = migrationSQL || projectState.pendingMigrationSQL;

    const calculatedState = { ...newState, tasks: calculatedTasks, pendingMigrationSQL: finalMigration };

    const newSnapshot: Snapshot = {
      id: `snap-${Date.now()}`,
      timestamp: Date.now(),
      tagName: tagName || `Update ${new Date().toLocaleTimeString()}`,
      intent: intent || AIIntent.UNDEFINED,
      agentRole: agent,
      aiReasoning: reasoning,
      projectState: calculatedState
    };

    setSnapshots(prev => [...prev, newSnapshot]);
    setProjectState(calculatedState);

    // Open Settings if a migration is proposed
    if (migrationSQL) {
        setIsSettingsOpen(true);
    }
  };

  const handleRestore = (snapshot: Snapshot) => {
    const calculatedTasks = calculateCPM(snapshot.projectState.tasks);
    setProjectState({ ...snapshot.projectState, tasks: calculatedTasks });
  };

  const handleNotesImport = (response: AIResponse) => {
    const newState: ProjectState = {
        ...projectState,
        sessions: response.sessions,
        tasks: response.tasks
    };
    handleAIUpdate(newState, response.reasoning, response.intent, "Imported Notes", AgentRole.DATA_MINER);
  };

  // Switch Project Logic
  const handleSelectProject = async (projectId: string) => {
     try {
        const response = await fetch(`${settings.backendUrl}/api/framework/load/${projectId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dbConfig: settings.dbConfig })
        });
        const data = await response.json();
        if (data.success) {
             const loadedState = data.projectState;
             // Recalc CPM immediately
            const cpmTasks = calculateCPM(loadedState.tasks);
            const finalState = { ...loadedState, tasks: cpmTasks };
            setProjectState(finalState);
            
            // Create initial snapshot for loaded project
            const newSnapshot: Snapshot = {
                id: `snap-db-load-${Date.now()}`,
                timestamp: Date.now(),
                tagName: `Loaded: ${finalState.name}`,
                intent: AIIntent.INITIAL_SETUP,
                agentRole: AgentRole.ARCHITECT,
                aiReasoning: "Loaded from Database via Sidebar.",
                projectState: finalState
            };
            setSnapshots(prev => [...prev, newSnapshot]);
        } else {
            alert("Error loading project: " + data.error);
        }
    } catch (err: any) {
        alert("Network error: " + err.message);
    }
  };

  // Import Handlers
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const importedState = await parseImportedJSON(file);
      const cpmTasks = calculateCPM(importedState.tasks);
      const finalState = { ...importedState, tasks: cpmTasks };
      
      setProjectState(finalState);
      
      const newSnapshot: Snapshot = {
        id: `snap-import-${Date.now()}`,
        timestamp: Date.now(),
        tagName: `Imported: ${finalState.name}`,
        intent: AIIntent.INITIAL_SETUP,
        agentRole: AgentRole.ARCHITECT,
        aiReasoning: "Imported from external JSON file.",
        projectState: finalState
      };
      setSnapshots(prev => [...prev, newSnapshot]);

      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (err) {
      alert("Failed to import JSON: " + err);
    }
  };

  const handleShareClick = () => {
    const text = generateTextSummary(projectState);
    setShareText(text);
    setIsShareOpen(true);
  };

  const getTasksBySession = () => {
    const grouped: Record<string, Task[]> = {};
    const sessionMap: Record<string, Session> = {};
    
    projectState.sessions.forEach(s => {
      grouped[s.id] = [];
      sessionMap[s.id] = s;
    });
    grouped['ungrouped'] = [];

    projectState.tasks.forEach(t => {
      if (t.sessionId && grouped[t.sessionId]) {
        grouped[t.sessionId].push(t);
      } else {
        grouped['ungrouped'].push(t);
      }
    });
    return { grouped, sessionMap };
  };

  const { grouped, sessionMap } = getTasksBySession();

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden text-slate-200">
      
      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="hidden" />

      <SettingsDialog 
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)} 
        settings={settings}
        onSave={setSettings}
        pendingMigrationSQL={projectState.pendingMigrationSQL}
        onClearMigration={() => setProjectState(prev => ({ ...prev, pendingMigrationSQL: undefined }))}
      />

      <ShareDialog 
        isOpen={isShareOpen} 
        onClose={() => setIsShareOpen(false)} 
        text={shareText} 
        project={projectState}
      />

      <NotesImporter 
        isOpen={isNotesImportOpen}
        onClose={() => setIsNotesImportOpen(false)}
        settings={settings}
        projectState={projectState}
        onImport={handleNotesImport}
      />

      {/* LEFT SIDEBAR: Project Navigator */}
      <ProjectSidebar 
         isOpen={isSidebarOpen}
         settings={settings}
         currentProjectId={projectState.id}
         onSelectProject={handleSelectProject}
         onRefresh={() => {}} 
      />

      {/* Main Work Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-indigo-500 bg-slate-900 flex items-center justify-between px-6 shadow-md shadow-indigo-500/10">
          <div className="flex items-center gap-3">
            <button 
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
            >
                {isSidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
            </button>
            <div className="bg-indigo-600 p-2 rounded-lg">
               <Layout className="w-5 h-5 text-white" />
            </div>
            <div>
                <h1 className="font-bold text-white tracking-tight flex items-center gap-2">
                    DevPath Pro 
                    <span className="bg-indigo-500 text-[10px] px-1.5 py-0.5 rounded animate-pulse">LIVE</span>
                </h1>
                <div className="flex items-center gap-2">
                    <p className="text-xs text-slate-400">Critical Path Management Engine</p>
                    <button 
                        onClick={() => window.location.reload()}
                        className="text-[8px] text-indigo-400 font-mono hover:underline"
                        title="Click to force reload if UI is stale"
                    >
                        v.{((process.env as any).BUILD_TIME || '').slice(11, 19)}
                    </button>
                </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
             {/* View Switcher */}
             <div className="flex bg-slate-800 rounded-lg p-1 mr-4">
                <button 
                  onClick={() => setViewMode('gantt')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-2 transition-all ${
                    viewMode === 'gantt' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <BarChartHorizontal className="w-4 h-4" /> Timeline
                </button>
                <button 
                  onClick={() => setViewMode('radar')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-2 transition-all ${
                    viewMode === 'radar' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Target className="w-4 h-4" /> Radar
                </button>
                <button 
                  onClick={() => setViewMode('ops')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-2 transition-all ${
                    viewMode === 'ops' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Activity className="w-4 h-4" /> Ops
                </button>
             </div>

             <div className="text-right hidden md:block">
                 <p className="text-xs font-semibold text-slate-300">Project: {projectState.name}</p>
                 <p className="text-[10px] text-slate-500 uppercase tracking-wider">{projectState.tasks.length} Tasks defined</p>
             </div>
             <div className="h-8 w-px bg-slate-800 mx-2"></div>
             
             {/* Actions */}
             <div className="flex items-center gap-2">
                <button 
                    onClick={() => setIsNotesImportOpen(true)}
                    className="p-2 text-amber-500 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    title="Import from Notes Pool (KVM8)"
                >
                    <StickyNote className="w-5 h-5" />
                </button>
                <button 
                    onClick={handleImportClick}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                    title="Import JSON"
                >
                    <Upload className="w-5 h-5" />
                </button>
                <button 
                    onClick={handleShareClick}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-all shadow-lg shadow-indigo-900/20 group"
                    title="Export or Share Project"
                >
                    <Share2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-semibold">Export & Share</span>
                </button>
             </div>

             <div className="h-8 w-px bg-slate-800 mx-2"></div>

             <button 
                onClick={() => setIsSettingsOpen(true)}
                className={`p-2 rounded-lg transition-colors relative ${
                    projectState.pendingMigrationSQL ? 'text-red-400 bg-red-900/20 hover:bg-red-900/40 animate-pulse' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                title="Configuration"
             >
                 <Settings className="w-5 h-5" />
                 {projectState.pendingMigrationSQL && (
                     <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                 )}
             </button>
          </div>
        </header>

        {/* Visualization Area */}
        <main className="flex-1 overflow-hidden bg-slate-950">
          
          {viewMode === 'ops' ? (
              <OpsCockpit settings={settings} />
          ) : (
            <div className="h-full overflow-y-auto p-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
                        <div className="flex items-center gap-3 mb-2">
                            <Clock className="w-4 h-4 text-blue-400" />
                            <h3 className="text-sm font-medium text-slate-400">Total Duration</h3>
                        </div>
                        <p className="text-2xl font-bold text-white">
                            {Math.max(0, ...projectState.tasks.map(t => t.earlyFinish || 0)).toFixed(1)} 
                            <span className="text-sm font-normal text-slate-500 ml-1">hours</span>
                        </p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
                        <div className="flex items-center gap-3 mb-2">
                            <Database className="w-4 h-4 text-emerald-400" />
                            <h3 className="text-sm font-medium text-slate-400">Snapshots Saved</h3>
                        </div>
                        <p className="text-2xl font-bold text-white">{snapshots.length}</p>
                    </div>
                    <div className="bg-slate-900 p-4 rounded-xl border border-slate-800">
                        <div className="flex items-center gap-3 mb-2">
                            <Layout className="w-4 h-4 text-red-400" />
                            <h3 className="text-sm font-medium text-slate-400">Critical Tasks</h3>
                        </div>
                        <p className="text-2xl font-bold text-white">
                            {projectState.tasks.filter(t => t.isCritical).length}
                        </p>
                    </div>
                    </div>

                    {/* Main Viz: Radar or Gantt */}
                    {viewMode === 'gantt' ? (
                        <GanttChart tasks={projectState.tasks} />
                    ) : (
                        <div className="h-[600px]">
                            <RadarView tasks={projectState.tasks} />
                        </div>
                    )}

                    {/* Task Table Grouped by Sessions */}
                    <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-800">
                            <h3 className="font-semibold text-white">Task Breakdown by Session</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-xs text-slate-500 uppercase bg-slate-800/50">
                                    <tr>
                                        <th className="px-6 py-3 w-32">ID</th>
                                        <th className="px-6 py-3">Task</th>
                                        <th className="px-6 py-3 w-32">Duration</th>
                                        <th className="px-6 py-3 w-32">Slack (Hrs)</th>
                                        <th className="px-6 py-3 w-32">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800">
                                    {/* Render Sessions First */}
                                    {projectState.sessions.map(session => (
                                    <React.Fragment key={session.id}>
                                        {/* Session Header - Distinct Section Style */}
                                        <tr className="bg-slate-900/50">
                                        <td colSpan={5} className="px-6 py-5">
                                            <div className="flex items-baseline gap-3">
                                            <span className="text-amber-500 font-bold text-sm">=={'>'}</span> 
                                            <span className="text-amber-400 font-bold text-base uppercase tracking-wider">{session.name}</span>
                                            {session.description && (
                                                <span className="text-slate-500 text-xs italic border-l border-slate-700 pl-3">
                                                    {session.description}
                                                </span>
                                            )}
                                            </div>
                                        </td>
                                        </tr>
                                        
                                        {/* Session Tasks */}
                                        {grouped[session.id]?.map(task => (
                                        <tr key={task.id} className="hover:bg-slate-800/30">
                                            <td className="px-6 py-3 font-mono text-slate-500 pl-12">{task.id}</td>
                                            <td className="px-6 py-3 font-medium text-slate-300">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                    {task.type === TaskType.PLACEHOLDER && (
                                                        <span title="Placeholder Task">
                                                            <BoxSelect className="w-3 h-3 text-slate-500" />
                                                        </span>
                                                    )}
                                                    <span className={task.type === TaskType.PLACEHOLDER ? "italic text-slate-500" : ""}>{task.name}</span>
                                                    {task.isCritical && <span className="ml-2 text-[10px] text-red-400 border border-red-900 bg-red-900/20 px-1 rounded">CRITICAL</span>}
                                                    {task.priority === 'HIGH' && <span className="ml-2 text-[10px] text-orange-400 border border-orange-900 bg-orange-900/20 px-1 rounded">HIGH</span>}
                                                    </div>
                                                    {task.description && (
                                                        <span className="text-xs text-slate-500 mt-1">{task.description}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-slate-400">{task.duration} {task.unit}</td>
                                            <td className="px-6 py-3 text-slate-400">{task.slack?.toFixed(2)}h</td>
                                            <td className="px-6 py-3">
                                                <span className={`text-xs px-2 py-1 rounded-full ${
                                                    task.status === 'COMPLETED' ? 'bg-green-900/30 text-green-400' :
                                                    task.status === 'IN_PROGRESS' ? 'bg-blue-900/30 text-blue-400' :
                                                    'bg-slate-700/30 text-slate-400'
                                                }`}>
                                                    {task.status}
                                                </span>
                                            </td>
                                        </tr>
                                        ))}
                                    </React.Fragment>
                                    ))}

                                    {/* Ungrouped Tasks */}
                                    {grouped['ungrouped']?.length > 0 && (
                                    <>
                                        <tr className="bg-slate-900/50">
                                        <td colSpan={5} className="px-6 py-5">
                                            <div className="flex items-baseline gap-3">
                                                <span className="text-amber-500 font-bold text-sm">=={'>'}</span> 
                                                <span className="text-amber-400 font-bold text-base uppercase tracking-wider">General / Unassigned</span>
                                            </div>
                                        </td>
                                        </tr>
                                        {grouped['ungrouped'].map(task => (
                                        <tr key={task.id} className="hover:bg-slate-800/30">
                                            <td className="px-6 py-3 font-mono text-slate-500 pl-12">{task.id}</td>
                                            <td className="px-6 py-3 font-medium text-slate-300">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center gap-2">
                                                        {task.type === TaskType.PLACEHOLDER && <BoxSelect className="w-3 h-3 text-slate-500"/>}
                                                        <span className={task.type === TaskType.PLACEHOLDER ? "italic text-slate-500" : ""}>{task.name}</span>
                                                        {task.isCritical && <span className="ml-2 text-[10px] text-red-400 border border-red-900 bg-red-900/20 px-1 rounded">CRITICAL</span>}
                                                        {task.priority === 'HIGH' && <span className="ml-2 text-[10px] text-orange-400 border border-orange-900 bg-orange-900/20 px-1 rounded">HIGH</span>}
                                                    </div>
                                                    {task.description && (
                                                        <span className="text-xs text-slate-500 mt-1">{task.description}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-slate-400">{task.duration} {task.unit}</td>
                                            <td className="px-6 py-3 text-slate-400">{task.slack?.toFixed(2)}h</td>
                                            <td className="px-6 py-3">
                                                <span className={`text-xs px-2 py-1 rounded-full ${
                                                    task.status === 'COMPLETED' ? 'bg-green-900/30 text-green-400' :
                                                    task.status === 'IN_PROGRESS' ? 'bg-blue-900/30 text-blue-400' :
                                                    'bg-slate-700/30 text-slate-400'
                                                }`}>
                                                    {task.status}
                                                </span>
                                            </td>
                                            </tr>
                                        ))}
                                    </>
                                    )}
                                    
                                    {projectState.tasks.length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                                                No tasks. Use the AI Architect to generate a plan, or Import a JSON.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
          )}
        </main>
      </div>

      {/* Right Sidebar: AI Architect */}
      <AIAssistant 
        projectState={projectState} 
        snapshots={snapshots}
        settings={settings}
        onUpdateProject={(newState, reasoning, intent, tagName, agent) => handleAIUpdate(newState, reasoning, intent, tagName, agent, newState.pendingMigrationSQL)}
        onRestoreSnapshot={handleRestore}
      />

    </div>
  );
};

export default App;
